import {
    Component,
    ElementRef,
    OnInit,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../services/alert/alert.service';
import { Router } from '@angular/router';
import { UserAuthService } from '../../../services/user/user-auth.service';
import {
    CreateAccountParams,
    IsExistOutput,
    SignInResult,
    SignInService,
} from '../sign-in/sign-in.service';
import { AuthService } from '../../../services/auth/auth.service';
import firebase from 'firebase/compat';
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
} from '@angular/common/http';
import { FormControl } from '../../../helpers/crud/edit/form-control/form-control.model';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { GoogleAddressService } from '../../../services/google-address/google-address.service';
import { debounceTime } from 'rxjs/operators';
import {
    Activity,
    ActivityCategory,
    ActivityFilterParams,
    ActivityService,
} from '../../spots/services/activity.service';
import { FormGroupHelper } from '../../../helpers/crud/edit/form-control/form-group-helper';
import { FilterResult } from '../../../helpers/crud/repository.service';
import { DateTime } from 'luxon';
import {
    OrganizationService,
    OutputSearchSiret,
} from '../../../services/organization/organization.service';
import { OrganizationTypeService } from './organization-type.service';
import { colorPalette } from '../../account-detail/colorPalette.model';
// Declare gTM dataLayer array.
declare global {
    interface Window { dataLayer: any[]; }
}
declare const fbq: any;

@Component({
    selector: 'app-login-dialog',
    templateUrl: './login-dialog.component.html',
    styleUrls: ['../account.scss', './login-dialog.component.scss'],
})
export class LoginDialogComponent implements OnInit {
    //#region Variables
    public window: Window = <Window>window;

    /**
     * permet d'afficher le bouton pour fermer la dialog
     * @deprecated Plus utilitsé actuellement
     */
    public isClosable: boolean = false;
    /**
     * Information de l'orga existante et du commercial de contact
     */
    public existingOrgaContact: OutputSearchSiret = null;

    //MARK: Constructor
    constructor(
        private readonly alert$: AlertService,
        public router: Router,
        private readonly dialogRef: MatDialogRef<LoginDialogComponent>,
        private readonly userAuth$: UserAuthService,
        private readonly signIn$: SignInService,
        private readonly auth$: AuthService,
        public formBuilder: UntypedFormBuilder,
        private readonly googleAddress$: GoogleAddressService,
        private readonly activity$: ActivityService,
        private readonly http: HttpClient,
        private readonly orga$: OrganizationService,
        public organizationTypes$: OrganizationTypeService
    ) { }

    /**
     * Current mode to apply to form `LoginMode` => Search by default
     */
    public currentMode: LoginMode = LoginMode.SearchLogin;

    //---- Login Infos ----

    /**
     * Login ou mail entré pour trouver le bon compte
     */
    public searchUserName: string;
    /**
     * Username or email to login
     */
    public username: string;
    /**
     * User's password
     */
    public password: string;
    /**
     * User's 2FA Code (SMS or APP)
     */
    public twoFACode: string;
    /**
     * Used to hide password input
     */
    public hidePassword: boolean = true;
    /**
     * True si l'utilisateur est en train de se connecter (pour gérer les états)
     */
    public isTryingToLogIn: boolean = false;
    /**
     * True si l'utilisateur est en train de créer
     */
    public isAccountCreating: boolean = false;
    /**
     * Message d'info pour entrer le code 2FA (SMS ou APP)
     */
    public twoFAInfo: string;
    /**
     * Identifiant unique Firebase
     */
    public firebaseUID: string = null;

    //---- Register Form ----
    /**
     * FormControls du formulaire d'inscription
     */
    public controls: { [key: string]: FormControl };
    /**
     * FormGroup contenant les controls du formulaire d'inscription
     */
    public loginForm: UntypedFormGroup;

    /**
     * Pour masquer ou non les chanmps de mdp à la cration de compte
     */
    public hideNewUserPass: boolean = true;

    /**
     * Loader de récupération de la position GPS
     */
    public isPositionLoading: boolean = false;
    /**
     * Options possible pour l'adresse
     */
    public addresseOptions: any[] = [];
    /**
     * `true si l'adresse est sélectionnée`
     */
    public isAddressSelected: boolean = false;

    /**
     * Liste des activités
     */
    public activityOptions: ActivityCategory[] = [];
    /**
     * Loader pour la liste des activités
     */
    public activitiesLoading: boolean = false;

    /**
     * Input html pour entrer le code 2FA
     */
    @ViewChildren('twoFAInput')
    public twoFAInputs: QueryList<any>;

    private current2FAInput: ElementRef;

    /**
     * Loader de la recherche de SIRET
     */
    public isSIRETLoading: boolean = false;
    /**
     * Liste des siret potentiels pour l'orga
     */
    public siretOptions: SirenEtablissement[];

    /**
     * Pour afficher le bouton de renvoi de mail de validation
     */
    public hasToValidMail: boolean = false;

    /** Conditions générales **/
    public isCGVok: boolean = false;
    public isCGUok: boolean = false;

    public companyTypeOption: any[] = [];

    public colorPalette: colorPalette = new colorPalette();

    //#endregion

    ngOnInit(): void {

        //Disconnect user to be sure
        this.userAuth$.signOut();

        //Création du formgroup et ajout du controle des mots de passe
        this.loginForm = this.formBuilder.group(this.initForm(), {
            validators: FormGroupHelper.checkConfirmPass,
        });
    }

    ngAfterViewInit(): void {
        this.twoFAInputs.changes.subscribe((comps) => {
            this.current2FAInput = comps.first;
        });
    }

    /**
     * Initialise le formulaire de création de compte
     * @returns la liste des controls
     */
    initForm(): { [key: string]: FormControl } {
        const firstName: FormControl = new FormControl('firstName').required(
            'Le prénom est requis'
        );

        const lastName: FormControl = new FormControl('lastName').required(
            'Le nom est requis'
        );

        const phoneNumber = new FormControl('phoneNumber')
            .maxLength(10)
            .required('')
            .pattern(
                /^[0-9\s]{5,14}$/,
                "Le numéro de téléphone n'est pas au bon format"
            );

        const email: FormControl = new FormControl('email').required(
            'Le mail est requis'
        );
        email.addValidator(Validators.email);

        const passwordCheckMsg: string = '';
        // 'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial';

        const password: FormControl = new FormControl('password')
            .minLength(8)
            .required('')
            .pattern(/[A-Z]/, passwordCheckMsg)
            .pattern(/[a-z]/, passwordCheckMsg)
            .pattern(/\d/, passwordCheckMsg)
            .pattern(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, passwordCheckMsg);

        const confirmPassword: FormControl = new FormControl('confirmPassword')
            .minLength(8)
            .required('')
            .pattern(/[A-Z]/, passwordCheckMsg)
            .pattern(/[a-z]/, passwordCheckMsg)
            .pattern(/\d/, passwordCheckMsg)
            .pattern(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, passwordCheckMsg);

        const orgaName: FormControl = new FormControl('orgaName').maxLength(50);

        const organizationTypeId: FormControl = new FormControl('organizationTypeId');

        const siret: FormControl = new FormControl('siret')
            .maxLength(14)
            .pattern(/\d{14}/, "Le format du SIRET n'est pas correct");

        siret.valueChanges.pipe(debounceTime(700)).subscribe((newSiret) => {
            if (this.controls?.siret.errors == null && newSiret != null) {
                this.checkSiret(newSiret);
            }
        });

        const orgaAddress: FormControl = new FormControl(
            'orgaAddress'
        ).maxLength(50);

        orgaAddress.valueChanges
            .pipe(debounceTime(700))
            .subscribe((searchedAddress) => {
                this.isAddressSelected = false;
                this.addresseOptions = [];

                if (orgaAddress.value != null && orgaAddress.value !== '') {
                    const addressAutoComplete =
                        new google.maps.places.AutocompleteService();

                    addressAutoComplete.getPlacePredictions(
                        {
                            input: searchedAddress,
                            componentRestrictions: { country: 'fr' },
                        },
                        (predictions, status) => {
                            if (status === 'OK') {
                                predictions.forEach((prediction) => {
                                    this.addresseOptions.push(
                                        prediction['description']
                                    );
                                });
                            }
                        }
                    );
                }
            });

        const orgaActivitysearch: FormControl = new FormControl(
            'orgaActivitysearch'
        );

        orgaActivitysearch.valueChanges
            .pipe(debounceTime(700))
            .subscribe((searchedActivity) => {
                this.activitiesLoading = true;

                this.activityOptions = [];

                const activityFilter = new ActivityFilterParams();
                activityFilter.name = searchedActivity;
                this.LoadActivities(activityFilter);
            });

        const orgaActivityId: FormControl = new FormControl(
            'orgaActivityId'
        ).required("Veuillez sélectionner une activité d'entreprise");
        this.organizationTypes$.getCompanyTypes().subscribe(res => {
            this.companyTypeOption = res.items;
        })

        this.controls = {
            firstName: firstName,
            lastName: lastName,
            phoneNumber: phoneNumber,
            email: email,
            password: password,
            confirmPassword: confirmPassword,
            orgaName: orgaName,
            siret: siret,
            orgaAddress: orgaAddress,
            orgaActivitysearch: orgaActivitysearch,
            orgaActivityId: orgaActivityId,
            organizationTypeId: organizationTypeId,
        };
        return this.controls;
    }

    /**
     * Vérifie si le siret est pas existant dans les clients
     * @param siretToCheck Siret
     */
    private checkSiret(siretToCheck: string) {
        if (siretToCheck != null) {
            this.orga$.searchExistantSIRET(siretToCheck).subscribe({
                next: (existingSiret: OutputSearchSiret) => {
                    if (existingSiret != null) {
                        //Warning à lever
                        this.alert$.show({
                            message: 'Siret déjà existant',
                            duration: 1700,
                        });
                        this.existingOrgaContact = existingSiret;
                        this.currentMode = LoginMode.AlreadyExistantSiret;
                    }
                },
                error: (err: HttpErrorResponse) => {
                    console.error(err);

                    this.alert$.show({
                        message:
                            "Erreur lors de la recherche de l'enseigne avec le siret " +
                            siretToCheck,
                        duration: 1700,
                    });
                },
            });
        }
    }

    /**
     * Charge la liste des activités
     * @param activityFilter Filtre des activité
     */
    private LoadActivities(activityFilter: ActivityFilterParams) {
        this.activity$.getActivityCategories(activityFilter).subscribe({
            next: (activityCategories: FilterResult<ActivityCategory>) => {
                this.activityOptions = activityCategories.items;

                //Remove 'Sans concurrence'
                this.activityOptions.forEach((category) => {
                    category.activities = category.activities.filter(
                        (curActivity) => curActivity.id !== 536
                    );
                });

                this.activitiesLoading = false;
            },

            error: (err) => {
                console.error(err);

                this.alert$.show({
                    message: 'Erreur lors de la récupération des activités',
                    duration: 1500,
                });

                this.activitiesLoading = false;
            },
        });
    }

    changeSiret(siretOpt: any) {
        this.controls.siret.setValue(siretOpt.siret);
        this.controls.siret.cleanServerError();
        setTimeout(() => {
            this.controls.siret.cleanServerError();
        }, 100);
        this.siretOptions = [];
    }

    private initSecondForm() {
        this.loginForm.get('orgaName').addValidators([Validators.required]);
        this.loginForm.get('siret').addValidators([Validators.required]);

        this.LoadActivities(new ActivityFilterParams());
    }

    public debug(param) {
    }

    /**
     * Ferme la popup et redirige vers la page qui ne nécessite pas d'être connecté
     */
    backToStore(): void {
        this.dialogRef.close();
        this.router.navigateByUrl('/store');
    }

    /**
     *
     * @returns Le titre de la dialog en fonction du mode actuel
     */
    getTitle(): string {
        switch (this.currentMode) {
            case LoginMode.RegisterPart1:
            case LoginMode.RegisterPart2:
            case LoginMode.ConfirmRegister:
                return 'Créer mon espace';
            case LoginMode.ForgotPassword:
                return 'Mot de passe oublié';
            case LoginMode.FinishedRegister:
                return 'Compte Wanview créé avec succès !';
            case LoginMode.AlreadyExistantSiret:
                return 'Entreprise déjà existante';
            case LoginMode.Login:
            case LoginMode.SearchLogin:
            default:
                return 'Se connecter';
        }
    }

    /**
     *
     * @returns Le sous-titre de la dialog en fonction du mode actuel
     */
    getSubTitle(): string {
        switch (this.currentMode) {
            case LoginMode.RegisterPart1:
            case LoginMode.RegisterPart2:
            case LoginMode.RegisterPart3:
            case LoginMode.ConfirmRegister:
                return "S'inscrire";
            case LoginMode.TwoFA:
                return 'Valider la double authentification';
            case LoginMode.ForgotPassword:
                return 'Réinitialiser son mot de passe';
            case LoginMode.FinishedRegister:
                return 'La suite se passe dans votre boite mail';
            case LoginMode.AlreadyExistantSiret:
                return 'Veuillez contacter votre commercial';

            case LoginMode.Login:
            case LoginMode.SearchLogin:
            default:
                return "Se connecter ou s'inscrire";
        }
    }

    /**
     * Recherche si un compte est existant à partir d'un login ou d'un mot de passe
     */
    searchExistingUser() {
        this.isTryingToLogIn = true;
        this.signIn$.checkLoginMail(this.searchUserName).subscribe({
            next: (isExistResponse: IsExistOutput) => {
                if (isExistResponse.canAccessWanview) {
                    //Peut accéder à wanview
                    this.currentMode = LoginMode.Login;
                    this.username = this.searchUserName;
                    //Focus sur le champ mdp
                    setTimeout(() => {
                        document.getElementById('pass')?.focus();

                    }, 0)
                } else if (!isExistResponse.emailConfirmed) {
                    this.hasToValidMail = true;
                    this.alert$.show({
                        message: "Veuillez d'abord valider votre adresse mail",
                        duration: 1700,
                    });
                } else {
                    this.alert$.show({
                        message: 'Ce compte est malheureusement bloqué',
                        duration: 1700,
                    });
                }
                this.isTryingToLogIn = false;
            },
            error: (err: HttpErrorResponse): void => {
                if (
                    err?.error?.status === 404 &&
                    err?.error?.title === 'Not Found'
                ) {
                    this.alert$.show({
                        message: 'Identifiant ou adresse mail inconnu',
                        duration: 1500,
                    });
                } else {
                    this.alert$.show({
                        message:
                            'Erreur lors de la récupération de login, veuillez réessayer plus tard',
                        duration: 1500,
                    });
                }

                console.error(err);
                this.isTryingToLogIn = false;
            },
        });
    }

    /**
     * Connexion de l'utilisateur ayant déjà un compte
     */
    signIn() {
        this.isTryingToLogIn = true;

        this.signIn$
            .signIn(this.username, this.password, this.twoFACode, null, true)
            .then((signInRes: SignInResult) => {
                this.handleSignInResult(signInRes);
            })
            .catch((err) => {
                console.error(err);

                if (err?.error === 'Votre compte est cloturé') {
                    this.alert$.show({
                        message:
                            'Impossible de se connecter : Votre compte Wanview est clôturé.',
                    });
                }

                this.isTryingToLogIn = false;
            });
    }

    /**
     * Gère la connexion d'un utilisateur (2FA, code mauvais mdp,...)
     * @param signInRes Résultat de la connexion
     */
    private async handleSignInResult(signInRes: SignInResult) {
        if (signInRes.mfa) {
            this.currentMode = LoginMode.TwoFA;
            setTimeout(() => { document.getElementById('twoFACode')?.focus(); }, 0);
            switch (signInRes.mfaMode) {
                case 'Sms':
                    this.twoFAInfo =
                        'Nous avons envoyé un SMS sur votre téléphone. Veuillez entrer le code pour vous connecter.';
                    //Focus on form element
                    this.current2FAInput.nativeElement.focus();
                    break;
                case 'App':
                    this.twoFAInfo =
                        "Veuillez saisir le code qui apparait sur votre application d'authentification.";
                    //Focus on form element

                    // this.current2FAInput.nativeElement.focus();
                    break;
                case 'NeedToBeDefined':
                    //Redirection vers sélection de la 2 FA
                    this.alert$.show({
                        message: "Veuillez définir un mode de double authentification",
                        duration: 1700
                    });
                    this.dialogRef.close();
                    this.router.navigateByUrl('/account/reset-password?setMfa=true');
                    break;

                default:
                    break;
            }
        }

        if (signInRes.token != null) {
            this.dialogRef.close();
            this.alert$.show({
                message: 'Connexion réussie',
                duration: 700,
            });

            const userInfo = await this.userAuth$.refreshUserInfos(
                true
            );

            this.userAuth$.initCrisp(userInfo);

            //25/02/25 Je remets ça pour refresh la page après login sinon on a l'impression que ça charge dans le vide
            window.location.reload();
        }

        if (signInRes.error) {
            this.twoFACode = '';
        }

        //Force refresh User's infos
        // const userInfo = await this.userAuth$.refreshUserInfos(true);

        // this.userAuth$.initCrisp(userInfo);

        this.isTryingToLogIn = false;
    }

    /**
     * Valide la 1ère partie du fomulaire d'inscription
     */
    continueRegister() {
        //Check if every required field is ok
        if (this.loginForm.valid) {
            //Check if email not alreadyUsed
            this.signIn$.checkLoginMail(this.controls.email.value).subscribe({
                next: (isExistResponse: IsExistOutput) => {
                    this.alert$.show({
                        message: 'Cette adresse mail est déjà utilisée',
                        duration: 1700,
                    });
                    this.sendEvent('email_deja_utilise');
                    this.controls.email.setErrors({ existingMail: true });
                },
                error: (err: HttpErrorResponse) => {
                    if (err.status === 404) {
                        //Aucun utilisateur avec ce login : OK
                        //continue
                        this.currentMode = LoginMode.RegisterPart2;

                        setTimeout(() => {
                            const dialogContent =
                                document.getElementById('dialogContent');

                            dialogContent.scrollTo({
                                top: 0,
                                behavior: 'smooth',
                            });
                        }, 20);

                        this.initSecondForm();
                        this.sendEvent('email_saisi');
                    } else {
                        console.error(err);
                        this.alert$.show({
                            message:
                                'Erreur lors de la vérification du mail, veuillez réessayer plus tard',
                            duration: 1700,
                        });
                    }
                },
            });
        } else {
            Object.keys(this.loginForm.controls).forEach((key: string) => {
                if (
                    !this.loginForm.get(key).valid &&
                    this.loginForm.get(key).pristine
                )
                    this.loginForm.get(key).markAsTouched();
            });
        }
    }

    /**
     *
     * @param selectedActivity Activité sélectionnée par l'utilisateur
     */
    selectActivity(selectedActivity: Activity) {
        this.controls.orgaActivityId.setValue(selectedActivity);
    }

    /**
     * Valide le formulaire sur l'entreprise
     */
    continueRegister2() {
        if (this.controls.orgaActivityId.value == null) {
            this.loginForm.get('orgaActivityId').setErrors({ required: true });
        }

        if (!this.loginForm.valid) {
            this.enableFormErrors();
        } else {
            this.checkSiret(this.controls.siret.value);
            if (this.currentMode === LoginMode.RegisterPart2) {
                this.currentMode = LoginMode.RegisterPart3
                this.sendEvent('compte_rempli');
            }
        }
    }

    // Valide le formulaire sur les couleur et envoi au recap
    continueRegister3() {
        if (this.currentMode === LoginMode.RegisterPart3) {
            this.currentMode = LoginMode.ConfirmRegister;
            this.sendEvent('compte_rempli');
        }
    }

    /**
     * Inscrit l'utilisateur
     */
    finishRegister() {
        if (!this.isCGVok || !this.isCGUok) {
            this.alert$.show({
                message: `Veuillez accepter les conditions générales d${this.isCGVok ? ' utilisation' : 'e ventes'
                    }`,
                duration: 700,
            });
            return;
        }

        if (
            this.loginForm.valid &&
            this.controls.orgaActivityId.value != null
        ) {
            this.isAccountCreating = true;
            this.googleAddress$
                .reverse(this.controls.orgaAddress.value)
                .subscribe((res: any) => {
                    let addrInfos = {
                        address: this.controls.orgaAddress.value,
                        cityName: '',
                        zipCode: '',
                    };

                    if (res.status === 'OK') {
                        let place = res.results[0];

                        const addr = place.formatted_address.split(',')[0];

                        const cityName = place.address_components.find(
                            (element) => {
                                return element.types.includes('locality');
                            }
                        )?.long_name;

                        const zipCode = place.address_components.find(
                            (element) => {
                                return element.types.includes('postal_code');
                            }
                        )?.long_name;

                        addrInfos = {
                            address: addr,
                            cityName: cityName,
                            zipCode: zipCode,
                        };
                    } else {
                        console.error('Error get Place GOOGLE');
                    }

                    const newUserInfos: CreateAccountParams = {
                        email: this.loginForm.get('email').value,
                        firebaseUID: this.firebaseUID ?? null,
                        firstName: this.loginForm.get('firstName').value,
                        lastName: this.loginForm.get('lastName').value,
                        organizationActivityId:
                            this.loginForm.get('orgaActivityId').value.id,
                        organizationName: this.loginForm.get('orgaName').value,
                        organizationTypeId: this.loginForm.get('organizationTypeId').value,
                        siretNumber: this.loginForm.get('siret').value,
                        password:
                            this.firebaseUID != null
                                ? null
                                : this.loginForm.get('password').value,
                        phoneNumber: this.loginForm.get('phoneNumber').value,
                        isOkCGU: this.isCGUok,
                        isOkCGV: this.isCGVok,
                        address: addrInfos.address,
                        cityName: addrInfos.cityName,
                        zipCode: addrInfos.zipCode ?? '01000',
                        colorPalette: this.colorPalette,
                    };

                    this.signIn$
                        .createStoreAccount(newUserInfos)
                        .then(() => {
                            //Redirect to homePage

                            this.isAccountCreating = false;
                            this.currentMode = LoginMode.FinishedRegister;
                            if (this.window != null) {
                                this.window.dataLayer = window.dataLayer || [];
                                this.window.dataLayer.push({
                                    event: 'compte_valide',
                                });
                                fbq('track', 'compte_valide', {});
                            }

                        })
                        .catch((err: HttpErrorResponse) => {
                            console.error(err);

                            if (
                                err?.error === "Nom d'utilisateur déjà existant"
                            ) {
                                this.alert$.show({
                                    message:
                                        'Erreur lors de la création de votre compte Wanview : ' +
                                        err.error,
                                    duration: 5000,
                                });
                            } else {
                                this.alert$.show({
                                    message:
                                        'Erreur lors de la création de votre compte Wanview',
                                    duration: 5000,
                                });
                            }

                            this.isAccountCreating = false;
                        });
                });
        } else {
            this.alert$.show({
                message: 'Erreur : Des données sont manquantes',
                duration: 700,
            });
            this.enableFormErrors();
        }
    }

    sendEvent(event: any) {
        if (this.window && this.window.dataLayer != null) {
            this.window.dataLayer = window.dataLayer || [];
            this.window.dataLayer.push({
                event: event,
            });
            fbq('track', event, {});

        }
        else {
            this.window.dataLayer = window.dataLayer || [];
            this.window.dataLayer.push({
                event: event,
            });
            fbq('track', event, {});

        }
    }

    private enableFormErrors() {
        Object.keys(this.loginForm.controls).forEach((key: string) => {
            if (
                !this.loginForm.get(key).valid &&
                this.loginForm.get(key).pristine
            )
                this.loginForm.get(key).markAsTouched();
        });
    }

    cancelConfirmRegister() {
        this.currentMode = LoginMode.RegisterPart2;
    }

    cancelRegisterPart2() {
        this.currentMode = LoginMode.RegisterPart1;
        this.loginForm.get('orgaName').setErrors(null);
        this.loginForm.get('siret').setErrors(null);
        this.loginForm.get('orgaAddress').setErrors(null);
    }

    cancelRegisterPart3() {
        this.currentMode = LoginMode.RegisterPart2;
    }

    cancelRegister() {
        this.loginForm.reset();
        this.firebaseUID = null;
        Object.keys(this.loginForm.controls).forEach((key: string) => {
            this.loginForm.get(key).setErrors(null);
        });
        this.currentMode = LoginMode.SearchLogin;
    }

    /**
     * Récupère l'adresse de l'utilisateur en fonction de sa position GPS
     * @returns L'adresse de l'utilisateur
     */
    getUserLocation() {
        this.isPositionLoading = true;
        if (navigator.geolocation) {
            return navigator.geolocation.getCurrentPosition(
                (pos: GeolocationPosition) => {
                    this.googleAddress$
                        .geocoding(pos.coords.latitude, pos.coords.longitude)
                        .subscribe((res: any) => {

                            this.loginForm
                                .get('orgaAddress')
                                .setValue(res.results[0].formatted_address, {
                                    emitEvent: false,
                                });
                            this.isPositionLoading = false;
                            this.isAddressSelected = true;
                        });
                },
                (err) => {
                    this.alert$.show({
                        message: 'Impossible de récupérer votre position GPS',
                        duration: 1500,
                    });
                    this.isPositionLoading = false;
                }
            );
        } else {
            this.alert$.show({
                message: 'Impossible de récupérer votre position GPS',
                duration: 1500,
            });
            this.isPositionLoading = false;
        }
    }

    /**
     * Récupère le ou la liste des SIRET correspondant au nom de l'entreprise passée en paramètre
     * @param orgaName Nom de l'entreprise à rechercher
     *
     * - Utilise [l'API SIREN](https://api.gouv.fr/documentation/sirene_v3)
     */
    getCompanySIRET(orgaName: string) {
        this.isSIRETLoading = true;

        if (orgaName !== '' && orgaName != null) {
            let sirenURL: string =
                'https://api.insee.fr/entreprises/sirene/siret?q=';

            let formatedOrgaName = orgaName.split(' ');

            for (let index = 0; index < formatedOrgaName.length; index++) {
                if (formatedOrgaName[index] === '') {
                    formatedOrgaName.splice(index, 1);
                }
            }
            const formatedName = formatedOrgaName.join('-');

            const currentDate = DateTime.local().toISODate();

            sirenURL += `(denominationUniteLegale:${formatedName} OR denominationUsuelle1UniteLegale: ${formatedName}) AND periode(etatAdministratifEtablissement:A)`;

            sirenURL +=
                '&nombre=5&champs=siret,denominationUniteLegale,codePostalEtablissement,libelleCommuneEtablissement';

            sirenURL += `&date=${currentDate}`;

            const header = {
                headers: new HttpHeaders().set(
                    'Authorization',
                    'Bearer e2094302-cd62-3f69-be7b-8ccc1fe765ab' //API SIREN Compte INSEE
                ),
            };

            this.http.get(sirenURL, header).subscribe({
                next: (res: SIRENOutput): void => {
                    this.siretOptions = res.etablissements;
                    this.isSIRETLoading = false;
                },
                error: (err) => {
                    console.error(err);

                    this.alert$.show({
                        message: 'Aucune entreprise trouvée avec ce nom',
                        duration: 700,
                    });
                    this.isSIRETLoading = false;
                },
            });
        } else {
            this.isSIRETLoading = false;
            this.alert$.show({
                message:
                    "Veuiller d'abord entrer le nom de l'entreprise pour lancer la recherche",
                duration: 1500,
            });

            this.controls.orgaName.markAsTouched();
        }
    }

    loginGoogle() {
        this.isTryingToLogIn = true;

        this.auth$.onGoogleAuth((userData) => {
            this.handleFirebaseUserInfos(userData);
        });
    }

    loginMicrosoft() {
        this.isTryingToLogIn = true;
        this.auth$.onMicrosoftAuth((userData?: firebase.User) => {
            this.handleFirebaseUserInfos(userData);
        });
    }

    private handleFirebaseUserInfos(userData?: firebase.User) {
        if (userData === null) {
            this.isTryingToLogIn = false;
            // this.alert$.show({
            //     message:
            //         "Erreur lors de l'authentification, veuillez réessayer plus tard",
            //     duration: 1700,
            // });
            return;
        }

        if (userData?.uid != null) {
            this.signIn$.checkFirebaseUID(userData.uid).subscribe({
                next: (isExistResponse: IsExistOutput) => {
                    if (isExistResponse.canAccessWanview) {
                        this.signIn$
                            .signIn(
                                userData.displayName,
                                userData.uid,
                                null,
                                userData.uid,
                                true
                            )
                            .then((signInFirebaseRes: SignInResult) => {
                                this.handleSignInResult(signInFirebaseRes);
                            });
                    } else if (!isExistResponse.emailConfirmed) {
                        this.hasToValidMail = true;
                        this.alert$.show({
                            message:
                                "Veuillez d'abord valider votre adresse mail",
                            duration: 1700,
                        });
                    } else {
                        this.alert$.show({
                            message: 'Ce compte est malheureusement bloqué',
                            duration: 1700,
                        });
                    }

                    //email exists !
                    this.isTryingToLogIn = false;
                },
                error: (err: HttpErrorResponse) => {
                    if (
                        err?.error?.status === 404 &&
                        err?.error?.title === 'Not Found'
                    ) {
                        this.alert$.show({
                            message:
                                "Aucun compte ne semble exister avec cet identifiant, création d'un nouveau compte",
                            duration: 1500,
                        });

                        this.firebaseUID = userData.uid;

                        this.currentMode = LoginMode.RegisterPart1;

                        this.controls.firstName.setValue(
                            userData.displayName.split(' ')[0]
                        );

                        this.controls.lastName.setValue(
                            userData.displayName.split(' ')[1] ?? undefined
                        );

                        this.controls.phoneNumber.setValue(
                            userData.phoneNumber
                        );

                        this.controls.email.setValue(userData.email);

                        this.isTryingToLogIn = false;
                    } else {
                        this.isTryingToLogIn = false;
                        this.alert$.show({
                            message:
                                'Impossible de vérifier le compte, veuillez réessayer plus tard',
                            duration: 1500,
                        });
                    }
                },
            });
        }
    }

    loginFacebook() {
        this.auth$.onFacebookAuth((userData: firebase.User) => {
            this.handleFirebaseUserInfos(userData);
        });
    }

    loginTwitter() {
        this.auth$.onTwitterAuth((userData: firebase.User) => {
            this.handleFirebaseUserInfos(userData);
        });
    }

    forgotPassword() {
        this.isTryingToLogIn = true;
        this.signIn$
            .forgotPassword(this.searchUserName)
            .then((s: SignInResult) => {
                if (!s.success) {
                    this.alert$.show({
                        message: s.message,
                        duration: 5000,
                    });
                }
                if (s.success) {
                    this.alert$.show({
                        message: s.message,
                        duration: 5000,
                    });
                }

                this.isTryingToLogIn = false;
                this.currentMode = 4;
            })
            .catch((err) => {
                console.error(err);

                this.alert$.show({
                    message:
                        "Une erreur s'est produite. Si le problème persiste, veuillez contacter le support.",
                    duration: 5000,
                });
                this.isTryingToLogIn = false;
            });
    }

    /**
     * Renvoi du mail de validation du mail d'un utilisateur
     */
    reSendValidationMail() {
        this.hasToValidMail = false;

        this.signIn$.newValidationMail(this.searchUserName).subscribe({
            next: () => {
                this.alert$.show({
                    message: 'Un Mail vient de vous être envoyé',
                    duration: 700,
                });
            },
            error: (err: HttpErrorResponse) => {
                if (err.status === 404) {
                    this.alert$.show({
                        message: `Aucun compte correspondant à '${this.searchUserName}'`,
                        duration: 1700,
                    });
                } else {
                    this.alert$.show({
                        message: "Erreur durant l'envoi du mail",
                        duration: 1500,
                    });
                }
            },
        });
    }

    goToLoginAfterRegister() {
        this.currentMode = LoginMode.SearchLogin;
        this.searchUserName = this.loginForm.get('email').value;
    }

    anchorActivity() {
        this.LoadActivities(new ActivityFilterParams());

        //scroll top

        setTimeout(() => {
            const dialogContent = document.getElementById('dialogContent');

            dialogContent.scrollTo({
                top: 395,
                behavior: 'smooth',
            });
        }, 200);
    }

    resetActivity() {
        this.controls.orgaActivityId.setValue(null, { emitEvent: false });

        this.controls.orgaActivitysearch.setValue(null, { emitEvent: false });

        this.anchorActivity();
    }

    sendMail(email: string) {
        const mailToString = `mailto:${email}?Subject=Obtention d'acces Wanview`;
        window.location.href = mailToString;
    }

    callPhone(phoneNumber: string) {
        window.location.href = `tel:${phoneNumber}`;
    }

    haveUppercase = (control: AbstractControl): boolean => {
        return /[A-Z]/.test(control.value);
    };
    haveLowercase = (control: AbstractControl): boolean => {
        return /[a-z]/.test(control.value);
    };
    haveNumber = (control: AbstractControl): boolean => {
        return /\d/.test(control.value);
    };
    haveSpecialCharacters = (control: AbstractControl): boolean => {
        return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(control.value);
    };

    getOrganizationTypeName(organizationTypeId: number) {
        let typeName = null;
        this.companyTypeOption.forEach(type => {
            if (type.id == organizationTypeId) {
                typeName = type.name;
            }
        });
        return typeName;
    }
}

export enum LoginMode {
    /**
     * Mode Connection
     */
    Login = 0,
    /**
     * Enregistrement du code app 2FA
     */
    TwoFA = 1,
    /**
     * Création d'un nouveau compte
     */
    RegisterPart1 = 2,
    /**
     * Création d'un nouveau compte part2
     */
    RegisterPart2 = 22,
    /**
     * Création d'un nouveau compte part3 facultative (couleurs)
     */
    RegisterPart3 = 21,
    /**
     * Confirmation des information de nouvau compte
     */
    ConfirmRegister = 23,
    /**
     * Info when user registered
     */
    FinishedRegister = 24,
    /**
     * Mot de passe oublié
     */
    ForgotPassword = 3,
    /**
     * 1ere étape, on entre le login ou le mail pour trouver l'utilisateur
     */
    SearchLogin = 4,
    /**
     * Message d'erreur quand Orga déjà existante
     */
    AlreadyExistantSiret = 5,
}

interface SirenEtablissement {
    siret: number;
    uniteLegale: {
        denominationUniteLegale: string;
    };
    adresseEtablissement: {
        codePostalEtablissement: string;
        libelleCommuneEtablissement: string;
    };
}

interface SIRENOutput {
    headers: any;
    etablissements: SirenEtablissement[];
}

